import React, { ReactElement, useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import GeneralTextInput from '../../../common/components/GeneralTextInput/generalTextInput'
import { TrashButton } from '../../../common/components/actions/trashBtn'
import { Excel, SheetDefault, Sheet } from '../../../common/types/api/excel'
import { ComponentCardForm } from '../../../common/types/cardForm'
import { card } from '../../../common/components/CardEditor/index'
import { getSpreadsheetSettings, setSpreadsheetSettings } from './helper'
import i18n from 'live/services/i18n'
import { ExcelColorsLabels, ExcelColorsValues } from '../Files/Excel/ColorSelector/colorDefinitions'
import SheetForm from './SheetForm'
import { Icon } from '../../../common/styles/global_styles'
import { editModeContentHook } from '../Content/hooks/editModeContent'
import { EditModeContent } from '../../../page/context/ReportContentCtx'

export interface SpreadsheetGeneratorProps {
    excel: Excel[]
}

function SpreadsheetGenerator({
    formObject,
    editMode,
    onChange,
    onCancel,
    onEditMode
}: ComponentCardForm<SpreadsheetGeneratorProps>): ReactElement {
    const { excel = [] } = formObject

    const [shouldResetSheetStore, setShouldResetSheetStore] = useState(false)
    const [currentFocusSheet, setCurrentFocusSheet] = useState<number>(-1)
    const previousFocusSheet = useRef(-1)
    useEffect(() => {
        if (editMode && (previousFocusSheet.current === undefined || previousFocusSheet.current != currentFocusSheet)) {
            setShouldResetSheetStore(true)
        }
        previousFocusSheet.current = currentFocusSheet
    }, [currentFocusSheet])
    editModeContentHook({
        onEditMode,
        onCancel,
        editMode,
        mode: EditModeContent.SPREADSHEET
    })
    const add = () => {
        onChange({
            ...formObject,
            excel: [
                ...excel,
                {
                    fileName: 'file name',
                    sheets: [SheetDefault]
                }
            ]
        })
    }
    const updateSheet = (spreadSheetId: number, tabIndex: number, sheet: Sheet) => {
        if (sheet === undefined) {
            delete excel[spreadSheetId].sheets[tabIndex]
            excel[spreadSheetId].sheets = excel[spreadSheetId].sheets.filter(s => !!s)
        } else {
            excel[spreadSheetId].sheets[tabIndex] = sheet
        }

        onChange({
            ...formObject,
            excel: excel
        })
    }

    const addSheet = (spreadSheetId: number) => {
        excel[spreadSheetId].sheets.push(SheetDefault)
        onChange({
            ...formObject,
            excel: excel
        })
    }

    const remove = (id: number) => {
        delete excel[id]
        onChange({
            ...formObject,
            excel: excel.filter(file => !!file)
        })
    }
    const updateName = (id: number, name: string) => {
        excel[id].fileName = name

        onChange({
            ...formObject,
            excel: excel
        })
    }
    if (excel?.length == 0) {
        return (
            <NotFile>
                {editMode ? (
                    <>
                        <Icon className={'far fa-empty-set'}> </Icon>
                        <label>No spreadsheet file added yet</label>
                        <button onClick={add}>+ {i18n('Add new spreadsheet')}</button>
                    </>
                ) : null}
            </NotFile>
        )
    }

    return (
        <div>
            {excel.map(({ fileName, sheets = [] }, index) => {
                if (!sheets && !Array.isArray(sheets)) {
                    sheets = []
                }
                return (
                    <div key={`excel-${index}`}>
                        {editMode ? (
                            <div
                                style={{
                                    marginLeft: '20px',
                                    marginTop: '10px'
                                }}
                            >
                                <TrashButton message={`Remove file '${fileName}'`} onClick={() => remove(index)} />
                            </div>
                        ) : null}
                        <div
                            style={{
                                padding: '12px'
                            }}
                        >
                            <GeneralTextInput
                                variant={'primary'}
                                border={true}
                                title={i18n('FILE NAME')}
                                error={false}
                                editMode={editMode}
                                placeholder={i18n('Name of the file')}
                                required={true}
                                value={fileName}
                                onChange={name => updateName(index, name)}
                            />
                        </div>
                        {sheets.map((sheet, indexSheet) => (
                            <div onFocus={() => setCurrentFocusSheet(indexSheet)}>
                                <SheetForm
                                    sheet={sheet}
                                    editMode={editMode}
                                    update={_sheet => updateSheet(index, indexSheet, _sheet)}
                                    shouldResetStore={indexSheet === 0 ? shouldResetSheetStore : false}
                                    resetStoreCallback={() => setShouldResetSheetStore(false)}
                                />
                            </div>
                        ))}

                        {editMode ? (
                            <AddSheet onClick={() => addSheet(index)}>
                                <label style={{ fontSize: '20px' }}>+</label>
                                <label> Add Spreadsheet Tab</label>
                            </AddSheet>
                        ) : null}
                    </div>
                )
            })}
            {/* {editMode ? <button onClick={add}>+ {i18n('Add new spreadsheet')}</button> : null} */}
        </div>
    )
}

export default card<SpreadsheetGeneratorProps>({
    title: i18n('ADD SPREADSHEET'),
    get: getSpreadsheetSettings,
    set: setSpreadsheetSettings,
    collapseStartsClosed: true,
    collapseOnHideClosed: true
})(SpreadsheetGenerator)

const NotFile = styled.div`
    height: 200px;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    ${Icon} {
        font-size: 32px;
        color: #bac0cb;
    }
    button {
        height: 24px;
        color: white;
        font-size: 14px;
        line-height: 16px;
        background: ${({ theme }) => theme['blue-500']};
        border: 0;
        border-radius: 3px;
        margin-bottom: 15px;
        margin-top: 20px;
        transition: 0.3s;
        padding: 4px;
        padding-left: 6px;
        padding-right: 6px;
        &:active {
            background: ${({ theme }) => theme['blue-700']};
        }

        &__pĺus {
            margin-right: 5px;
            font-size: 15px;
        }
    }
`
const Card = styled.div``
const AddSheet = styled.button`
    height: 32px;
    width: 100%;
    padding: 2px;
    background-color: #e1f0ff;
    border: 1px solid rgb(186, 192, 203);

    label {
        color: #37a3ef;
        font-size: 15px;
        display: inline;
    }
`

const Generator = styled.div<SpreadsheetGeneratorProps>`
    font-family: Source Sans Pro;
    width: 940px;
    height: 628px;
    border: 1px solid #c8ccd4;

    .fileName {
        display: flex;
        width: 100%;
        background-color: white;
        height: 100px;
        padding: 24px 24px;

        label {
            color: #364159;
        }

        input {
            margin-top: auto;
            color: #c8ccd4;
            border: 1px solid;
            border-radius: 3px;
            width: 232px;
            height: 32px;
        }
    }
    .tabName {
        display: flex;
        width: 100%;
        height: 28px;
        align-items: center;
        padding-left: 24px;
        background-color: #eeeeee;
        span {
            font-weight: 600;
            font-size: 11px;
            line-height: 12px;
        }
    }
`
